import React, { useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { IconButton, Menu, MenuItem, Modal, Box, Button } from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../config/URL";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import GlobalDelete from "../../../components/common/GlobalDelete";

const StudentViewCourse = ({ data, getData }) => {
  const navigate = useNavigate();

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#fff",
    borderRadius: "6px",
    padding: "20px",
    width: "300px",
  };

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [editRow, setEditRow] = useState(null);

  const handleMenuClose = () => setMenuAnchor(null);

  const formatTimeTo12Hour = (time) => {
    if (!time) return "";
    const [hours, minutes] = time.split(":").map(Number);
    const formattedTime = new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(new Date(0, 0, 0, hours, minutes));
    return formattedTime;
  };
  const handleEditClick = () => {
    if (!selectedRow) return;

    setEditRow(selectedRow);
    setStartDate(
      selectedRow.startDate ? new Date(selectedRow.startDate) : null,
    );
    setOpenModal(true);
    handleMenuClose();
  };

  const handleUpdate = async () => {
    try {
      const courseDetailId = editRow?.id || selectedRow?.id;
      // console.log("Id is kishore::", courseDetailId);

      if (!courseDetailId) {
        toast.error("Course ID not found");
        return;
      }

      if (!startDate) {
        toast.error("Please select a start date");
        return;
      }

      const formData = new FormData();
      formData.append("startDate", startDate.toISOString().split("T")[0]);

      await api.put(`updateStartDate/${courseDetailId}`, formData);

      toast.success("Course start date updated successfully");
      setOpenModal(false);
      setSelectedRow(null);
      setEditRow(null);
      if (typeof getData === "function") {
        await getData();
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update course start date");
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        enableSorting: true,
        enableHiding: true,
        size: 40,
        cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      // {
      //   accessorKey: "id",
      //   header: "",
      //   size: 10,
      //   Cell: ({ row }) => (

      //     <IconButton
      //       onClick={(e) => {
      //         e.stopPropagation();
      //         setMenuAnchor(e.currentTarget);
      //         setSelectedRow(row.original);
      //       }}
      //     >
      //       <MoreVertIcon />
      //     </IconButton>
      //   ),
      // },
      {
        accessorKey: "id",
        header: "",
        size: 10,
        Cell: ({ row }) => {
          const status = row.original.status?.trim().toUpperCase();

          const showIcon =
            !status || status === "YET_TO_START" || status === "PURSUING";

          if (!showIcon) return null;

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
                setSelectedRow(row.original);
              }}
            >
              <MoreVertIcon />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "status",
        enableHiding: true,
        size: 50,
        header: "Status",
        Cell: ({ row }) => {
          const status = row.original.status?.trim().toUpperCase();

          switch (status) {
            case "END_CLASS":
              return (
                <span className="badge bg-danger fw-light">End Class</span>
              );

            case "CHANGE_CLASS":
              return (
                <span className="badge bg-primary fw-light">Change Class</span>
              );

            case "WITHDRAWN":
              return (
                <span className="badge bg-warning fw-light">Withdrawn</span>
              );

            case "PURSUING":
              return <span className="badge bg-info fw-light">Pursuing</span>;

            case "COMPLETED":
              return (
                <span className="badge bg-success fw-light">Completed</span>
              );

            case "GRADUATE":
              return (
                <span className="badge bg-success fw-light">Graduate</span>
              );

            case "SUSPEND":
              return (
                <span className="badge bg-secondary fw-light">Suspend</span>
              );

            case "YET_TO_START":
              return (
                <span className="badge bg-warning fw-light">Yet To Start</span>
              );

            default:
              return null; // ✅ no badge for null / empty / unknown
          }
        },
      },
      { accessorKey: "centerName", enableHiding: true, header: "Center Name" },
      {
        accessorKey: "course",
        enableHiding: true,
        header: "Course",
      },
      {
        accessorKey: "className",
        enableHiding: true,
        header: "Class Name",
      },
      {
        accessorKey: "teacher",
        enableHiding: true,
        header: "Teacher",
      },
      {
        accessorKey: "startDate",
        enableHiding: true,
        size: 50,
        header: "Start Date",
      },
      {
        accessorKey: "endDate",
        enableHiding: true,
        size: 50,
        header: "End Date",
      },
      {
        accessorKey: "startTime",
        header: "Start Time",
        Cell: ({ cell }) => formatTimeTo12Hour(cell.getValue()),
      },
      {
        accessorKey: "endTime",
        header: "End Time",
        Cell: ({ cell }) => formatTimeTo12Hour(cell.getValue()),
      },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      {
        accessorKey: "updatedBy",
        header: "Updated By",
        Cell: ({ cell }) => cell.getValue() || "",
      },
    ],
    [],
  );

  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            "&.Mui-disabled .MuiSwitch-track": {
              backgroundColor: "#f5e1d0",
              opacity: 1,
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: "#eb862a",
            },
          },
          track: {
            backgroundColor: "#e0e0e0",
          },
          thumb: {
            color: "#eb862a",
          },
          switchBase: {
            "&.Mui-checked": {
              color: "#eb862a",
            },
            "&.Mui-checked + .MuiSwitch-track": {
              backgroundColor: "#eb862a",
            },
          },
        },
      },
    },
  });

  const today = new Date();

  const getWeekDay = (date) => date.getDay(); // 0–6 (Sun–Sat)

  // Allowed weekday = original start date weekday
  const allowedWeekDay = selectedRow?.startDate
    ? new Date(selectedRow.startDate).getDay()
    : null;

  // Disable unwanted dates
  const filterStartDate = (date) => {
    if (!allowedWeekDay && allowedWeekDay !== 0) return true;

    return getWeekDay(date) === allowedWeekDay;
  };

  return (
    <>
      <ThemeProvider theme={theme}>
        <MaterialReactTable
          columns={columns}
          data={data}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
        />
      </ThemeProvider>

      {/* ACTION MENU */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            minWidth: 70,
          },
        }}
      >
        {/* EDIT */}
        <MenuItem
          onClick={handleEditClick}
          sx={{
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          Edit
        </MenuItem>

        {/* DELETE */}
        <MenuItem
          sx={{
            justifyContent: "center",
            textAlign: "center",
            width: "100%",
          }}
        >
          <Box sx={{ width: "100%", textAlign: "center" }}>
            <GlobalDelete
              path={`deleteStudentCourseDetail/${selectedRow?.id}`}
              onDeleteSuccess={() => {
                handleMenuClose();
                if (typeof getData === "function") {
                  getData();
                }
              }}
              onOpen={handleMenuClose}
            />
          </Box>
        </MenuItem>
      </Menu>

      {/* EDIT MODAL */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box sx={modalStyle}>
          {/* HEADER */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Edit Course Dates</h6>

            <IconButton size="small" onClick={() => setOpenModal(false)}>
              ✕
            </IconButton>
          </div>

          {/* START DATE */}
          <div className="mb-3">
            <label className="form-label">Start Date</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="yyyy-MM-dd"
              popperPlacement="bottom-start"
              wrapperClassName="studentView-react-datepicker-wrapper"
              popperClassName="studentView-react-datepicker-popper"
              calendarClassName="studentView-react-datepicker-calendar"
              className="form-control"
              placeholderText="Select start date"
              minDate={today}
              maxDate={
                selectedRow?.endDate ? new Date(selectedRow.endDate) : null
              }
              filterDate={filterStartDate}
              renderCustomHeader={({
                monthDate,
                decreaseMonth,
                increaseMonth,
              }) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 10px",
                    fontWeight: 500,
                  }}
                >
                  <button
                    type="button"
                    onClick={decreaseMonth}
                    className="btn btn-sm btn-light"
                  >
                    ‹
                  </button>

                  <span>
                    {monthDate.toLocaleString("default", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>

                  <button
                    type="button"
                    onClick={increaseMonth}
                    className="btn btn-sm btn-light"
                  >
                    ›
                  </button>
                </div>
              )}
            />
          </div>

          {/* END DATE */}
          <div className="mb-3">
            <label className="form-label">End Date</label>
            <input
              type="text"
              className="form-control"
              value={selectedRow?.endDate || ""}
              readOnly
            />
          </div>

          {/* ACTION BUTTONS */}
          <div className="d-flex justify-content-between gap-2 mt-3">
            <button
              className="btn btn btn-border btn-sm"
              onClick={() => setOpenModal(false)}
            >
              Cancel
            </button>

            <button className="btn btn-button btn-sm" onClick={handleUpdate}>
              Update
            </button>
          </div>
        </Box>
      </Modal>
    </>
  );
};

export default StudentViewCourse;
